import { faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons/faCloudArrowUp'
import { Anchor, Box, Flex, Text } from '@mantine/core'
import { Dropzone, type DropzoneProps, MIME_TYPES } from '@mantine/dropzone'
import { clsx } from 'clsx'

import { useTranslation, Trans } from '@flecto/translations'

import { Icon } from '../Icon/Icon'
import styles from './styles.module.css'

export { MIME_TYPES } from '@mantine/dropzone'
export type FileUploaderProps = Omit<DropzoneProps, 'children'>

export function FileUploader({
  maxSize = 10,
  accept = [MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.pdf],
  className,
  ...props
}: FileUploaderProps) {
  const { t } = useTranslation('shared', { keyPrefix: 'file_uploader' })
  const classes = clsx(styles.fileUploader, className)

  const acceptedTypes = Array.isArray(accept) ? accept : [String(accept)]
  const acceptedTypesExtensions = acceptedTypes.map((type) =>
    type
      .replace(/application\/msword/, 'doc')
      .replace(/image\/svg\+xml/, 'svg')
      .replace(/application\/vnd.ms-excel/, 'xls')
      .replace(/application\/vnd.ms-powerpoint/, 'ppt')
      .replace(/application\/vnd.microsoft.portable-executable/, 'exe')
      .replace(
        /application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet/,
        'xlsx'
      )
      .replace(
        /application\/vnd.openxmlformats-officedocument.presentationml.presentation/,
        'pptx'
      )
      .replace(
        /application\/vnd.openxmlformats-officedocument.wordprocessingml.document/,
        'docx'
      )
      .replace(/(image|application)(\/)/, '')
      .toUpperCase()
  )

  return (
    <Dropzone
      radius='md'
      {...props}
      maxSize={maxSize * 1024 ** 2}
      className={classes}
      accept={accept}
      w='100%'
    >
      <Flex
        align='center'
        justify='center'
        direction='column'
        style={{ pointerEvents: 'none' }}
      >
        <Dropzone.Accept>
          <Icon
            icon={faCloudArrowUp}
            withCircleBackground
            size='lg'
            color='green'
            data-testid='icon-accept'
          />
        </Dropzone.Accept>

        <Dropzone.Reject>
          <Icon
            icon={faCloudArrowUp}
            color='red'
            withCircleBackground
            data-testid='icon-reject'
          />
        </Dropzone.Reject>

        <Dropzone.Idle>
          <Icon
            icon={faCloudArrowUp}
            data-testid='icon-idle'
            withCircleBackground
            size='lg'
          />
        </Dropzone.Idle>

        <Box mt='md'>
          <Text c='dimmed' size='sm' ta='center' data-testid='title'>
            <Trans
              ns='shared'
              i18nKey='file_uploader.title'
              components={{
                link_choose: <Anchor fw={500} />,
              }}
            />
          </Text>
          <Text
            c='dimmed'
            size='xs'
            ta='center'
            mt='sm'
            data-testid='description'
          >
            {t('description', {
              replace: {
                allowed_types: acceptedTypesExtensions.join(', '),
                file_size: `${maxSize}MB`,
              },
            })}
          </Text>
        </Box>
      </Flex>
    </Dropzone>
  )
}
